import React from "react";
import { fmtINR, api } from "@/lib/api";
import { ResponsiveContainer, Tooltip, Cell, PieChart, Pie, Legend } from "recharts";
import { TrendUp, TrendDown, Wallet, Receipt, ClockClockwise, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import BankAccountsCards from "@/components/BankAccountsCards";
import OtherIncomeSection from "@/components/OtherIncomeSection";

const STATUS_NARRATIVE = {
  healthy: {
    title: "Cash position is HEALTHY",
    body: "Projected inflows comfortably cover committed outflows for the next horizon. No additional cash arrangement required.",
    band: "cash-healthy-tile",
    blink: false,
  },
  tight: {
    title: "Cash position is TIGHT",
    body: "Net position is positive but margin is thin. Consider deferring discretionary outflows or accelerating vendor collections.",
    band: "cash-tight-tile",
    blink: false,
  },
  shortfall: {
    title: "Cash SHORTFALL projected",
    body: "Committed outflows exceed available cash + projected inflows. Arrange working-capital injection or stagger payouts.",
    band: "cash-tight-tile",
    blink: false,
  },
};

const INFLOW_COLORS  = ["#22d3ee", "#0ea5e9", "#67e8f9", "#7dd3fc", "#06b6d4"];
const OUTFLOW_COLORS = ["#0e7490", "#0891b2", "#155e75", "#0369a1", "#075985", "#164e63"];

export default function CashHealthTab({ kpi, refreshKey, onRefresh }) {
  if (!kpi) return <div className="text-sm text-slate-500 py-12 text-center">Loading cash health…</div>;
  const meta = STATUS_NARRATIVE[kpi.status];

  const inflowData = Object.entries(kpi.breakdown?.inflows_by_source || {}).map(([k, v]) => ({ name: k, value: v }));
  const inflowItemsMap = kpi.breakdown?.inflow_items || {};
  const outflowData = Object.entries(kpi.breakdown?.outflows_by_category || {}).map(([k, v]) => ({ name: k, value: v }));

  // Most-recent `uploaded_at` across ALL inflow items, used as "Last updated"
  // freshness pill on the Inflows-by-source header.
  const inflowLastUpload = (() => {
    let latest = null;
    for (const items of Object.values(inflowItemsMap)) {
      for (const it of items || []) {
        const u = it?.uploaded_at;
        if (u && (!latest || u > latest)) latest = u;
      }
    }
    return latest;
  })();
  const inflowLastUploadLabel = (() => {
    if (!inflowLastUpload) return null;
    const d = new Date(inflowLastUpload);
    if (isNaN(d.getTime())) return null;
    const time = d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
    const toDayNum = (dt) => dt.getFullYear() * 10000 + (dt.getMonth() + 1) * 100 + dt.getDate();
    const diffDays = toDayNum(new Date()) - toDayNum(d);
    if (diffDays === 0) return `Updated today, ${time}`;
    if (diffDays === 1) return `Updated yesterday, ${time}`;
    const days = Math.floor((Date.now() - d.getTime()) / 86400000);
    return `Updated ${days}d ago`;
  })();

  return (
    <div className="space-y-6" data-testid="cash-health-tab">
      {/* Narrative band — neutral / colorless */}
      <div className="rounded-md bg-white border border-slate-200 p-5 shadow-sm relative overflow-hidden" data-testid="cash-narrative" data-status={kpi.status}>
        <div className="relative flex items-start gap-4">
          <div className="h-10 w-10 grid place-items-center rounded-sm bg-slate-100 text-slate-600">
            <Wallet size={22} weight="duotone" />
          </div>
          <div className="flex-1">
            <div className="font-heading font-black text-xl tracking-tight text-slate-900">{meta.title}</div>
            <div className="text-sm mt-2 max-w-3xl leading-relaxed text-slate-600">{meta.body}</div>
          </div>
        </div>
      </div>

      {/* Bank accounts + Tally outstanding (same grid, same card size) */}
      <BankAccountsCards
        refreshKey={refreshKey}
        extraCard={
          kpi.breakdown?.tally_outstanding
            ? <TallyOutstandingCard meta={kpi.breakdown.tally_outstanding} onCleared={onRefresh} />
            : null
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Inflow breakdown */}
        <div className="bg-white rounded-md border border-slate-200 p-5 shadow-sm" data-testid="inflow-chart">
          <div className="flex items-center justify-between mb-3 gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <div className="h-7 w-7 rounded-sm grid place-items-center bg-emerald-100 text-emerald-700"><TrendUp size={15} weight="bold" /></div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Inflows by source</div>
                <div className="font-mono tabular-nums text-base text-emerald-700 font-bold mt-0.5">{fmtINR(kpi.projected_inflow)}</div>
              </div>
            </div>
            {inflowLastUploadLabel && (
              <div
                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border border-cyan-700/40 bg-cyan-950/30 text-[10px] uppercase tracking-wider font-bold text-cyan-200 whitespace-nowrap"
                title={`Last upload at ${new Date(inflowLastUpload).toLocaleString("en-IN")}`}
                data-testid="inflow-last-update"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-cyan-300 pulse-dot" />
                {inflowLastUploadLabel}
              </div>
            )}
            {inflowData.length > 0 && (
              <button
                onClick={async () => {
                  if (!window.confirm("Clear ALL projected inflows (Swiggy + Zomato + PetPooja)? Re-upload the source files to repopulate.")) return;
                  try {
                    const r = await api.delete("/projected-inflows/clear");
                    toast.success(`Cleared ${r.data.deleted} inflow row(s)`);
                    onRefresh?.();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Failed to clear inflows");
                  }
                }}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border border-rose-200 bg-white text-rose-600 hover:bg-rose-50 hover:text-rose-700 text-[9px] uppercase tracking-wider font-bold"
                data-testid="inflow-clear-btn"
                title="Clear all projected inflows — next upload starts fresh"
              >
                <Trash size={10} weight="bold" />
                Clear
              </button>
            )}
          </div>
          {inflowData.length === 0 ? <div className="h-56 flex items-center justify-center"><Empty /></div> : (
            <div className="space-y-2">
              {inflowData.map((row, i) => {
                const items = inflowItemsMap[row.name] || [];
                const maxValue = Math.max(...inflowData.map((d) => d.value)) || 1;
                const pct = (row.value / maxValue) * 100;
                const color = INFLOW_COLORS[i % INFLOW_COLORS.length];
                return (
                  <div
                    key={row.name}
                    className="relative group rounded-sm border border-slate-100 hover:border-slate-300 hover:shadow-sm transition-all bg-white"
                    data-testid={`inflow-row-${row.name}`}
                  >
                    <div className="flex items-center gap-3 px-3 py-2">
                      <div className="w-20 shrink-0 text-xs font-bold text-slate-700 capitalize truncate" title={row.name}>{row.name}</div>
                      <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
                      </div>
                      <div className="w-24 text-right font-mono tabular-nums text-sm font-bold text-slate-900">{fmtINR(row.value)}</div>
                      {items.length > 0 && (
                        <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-600 shrink-0">
                          {items.length} {items.length === 1 ? "item" : "items"}
                        </span>
                      )}
                    </div>

                    {items.length > 0 && (
                      <div
                        className="pointer-events-none absolute left-0 right-0 bottom-full mb-2 z-20 hidden group-hover:block rounded-md border border-slate-200 bg-white shadow-lg p-3"
                        data-testid={`inflow-tooltip-${row.name}`}
                      >
                        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-2 flex items-center gap-2">
                          <span>{row.name} pending payouts</span>
                          <span className="font-mono text-slate-500">· {items.length}</span>
                        </div>
                        <div className="space-y-2 max-h-72 overflow-y-auto">
                          {items.map((it, j) => (
                            <div key={j} className="flex items-start gap-3 text-xs border-b border-slate-100 last:border-b-0 pb-2 last:pb-0">
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold text-slate-900 truncate" title={it.outlet}>{it.outlet || "—"}</div>
                                <div className="text-slate-500 text-[10px] mt-0.5 font-mono">
                                  {it.res_id && <>Res ID <span className="font-bold text-slate-700">{it.res_id}</span> · </>}
                                  Credits on <span className="font-bold text-slate-700">{it.date}</span>
                                </div>
                                {it.period_start && it.period_end && (
                                  <div className="text-slate-400 text-[10px] mt-0.5">
                                    Week: {it.period_start} → {it.period_end}
                                  </div>
                                )}
                              </div>
                              <div className="font-mono tabular-nums font-bold text-emerald-700 shrink-0">{fmtINR(it.amount)}</div>
                            </div>
                          ))}
                        </div>
                        <div className="absolute left-8 top-full -mt-px w-2 h-2 bg-white border-r border-b border-slate-200 rotate-45" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Outflow donut */}
        <div className="bg-white rounded-md border border-slate-200 p-5 shadow-sm" data-testid="outflow-chart">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded-sm grid place-items-center bg-rose-100 text-rose-700"><TrendDown size={15} weight="bold" /></div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Outflows by category</div>
                <div className="font-mono tabular-nums text-base text-rose-700 font-bold mt-0.5">{fmtINR(kpi.committed_outflow)}</div>
              </div>
            </div>
          </div>
          <div className="h-56" style={{ minHeight: 224 }}>
            {outflowData.length === 0 ? <Empty /> : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={outflowData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={2} strokeWidth={2} stroke="#fff">
                    {outflowData.map((_, i) => <Cell key={i} fill={OUTFLOW_COLORS[i % OUTFLOW_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmtINR(v)} contentStyle={{ fontSize: 12, border: '1px solid #e2e8f0', borderRadius: 4 }} />
                  <Legend wrapperStyle={{ fontSize: 11, fontFamily: 'IBM Plex Sans' }} iconType="circle" />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Cash math */}
      <div className="bg-white rounded-md border border-slate-200 p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-5">
          <div className={`h-6 w-1 rounded-sm bg-gradient-to-b ${meta.grad}`} />
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Cash math · next {kpi.horizon_days} days</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <MathCell label="Bank balance" value={kpi.bank_balance} sign="+" color="text-indigo-700" bg="bg-indigo-50" />
          <MathCell label="Projected inflow" value={kpi.projected_inflow} sign="+" color="text-emerald-700" bg="bg-emerald-50" />
          <MathCell label="Committed outflow" value={kpi.committed_outflow} sign="−" color="text-rose-700" bg="bg-rose-50" />
          <MathCell label="Net position" value={kpi.net_position} sign="=" color={meta.pillText} bg={meta.pillBg} border={meta.pillBorder} bold />
        </div>
      </div>

      <OtherIncomeSection refreshKey={refreshKey} onChanged={onRefresh} />
    </div>
  );
}

function Empty() {
  return <div className="h-full flex items-center justify-center text-xs text-slate-400">No data — upload or load sample data</div>;
}

function MathCell({ label, value, sign, color, bg, border, bold }) {
  return (
    <div className={`rounded-md p-4 ${bg || ""} ${border ? `border ${border}` : ""}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold flex items-center gap-2">
        <span className="text-slate-400 font-mono text-base">{sign}</span> {label}
      </div>
      <div className={`font-mono tabular-nums text-xl mt-2 ${color || 'text-slate-900'} ${bold ? 'font-bold' : 'font-semibold'}`}>
        {fmtINR(value)}
      </div>
    </div>
  );
}

function TallyOutstandingCard({ meta, onCleared }) {
  const [clearing, setClearing] = React.useState(false);
  const handleClear = async () => {
    if (!window.confirm("Clear ALL Tally outstanding data? Re-uploading a fresh Tally file will repopulate this.")) return;
    setClearing(true);
    try {
      const r = await api.delete("/outstanding/clear");
      toast.success(`Cleared ${r.data.deleted} vendor row(s)`);
      onCleared?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to clear Tally outstanding");
    } finally {
      setClearing(false);
    }
  };
  const label = (() => {
    if (!meta?.last_upload_at) return "Never";
    const d = new Date(meta.last_upload_at);
    if (isNaN(d.getTime())) return "—";
    const time = d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
    const datePart = d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" });
    // Compare calendar days (local) — never label yesterday's upload as Today.
    const toDayNum = (dt) => dt.getFullYear() * 10000 + (dt.getMonth() + 1) * 100 + dt.getDate();
    const diffDays = toDayNum(new Date()) - toDayNum(d);
    if (diffDays === 0) return `Today, ${time}`;
    if (diffDays === 1) return `Yesterday, ${time}`;
    const daysAgo = Math.floor((Date.now() - d.getTime()) / 86400000);
    if (daysAgo <= 7) return `${daysAgo}d ago`;
    return `${datePart}, ${time}`;
  })();
  const stale = (() => {
    if (!meta?.last_upload_at) return true;
    const d = new Date(meta.last_upload_at);
    return ((Date.now() - d.getTime()) / 86400000) > 7;
  })();
  const tone = stale ? "text-amber-500" : "text-emerald-600";
  return (
    <div
      className="relative rounded-md border border-slate-200 bg-white overflow-visible shadow-sm hover:shadow-md transition-shadow"
      data-testid="tally-outstanding-card"
    >
      <div className="h-1 bg-gradient-to-r from-amber-400 to-orange-500 rounded-t-md" />
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 grid place-items-center rounded-sm bg-amber-100 text-amber-700">
                <Receipt size={14} weight="duotone" />
              </div>
              <div className="text-sm font-semibold text-slate-900 truncate">Tally Outstanding</div>
            </div>
          </div>
          {meta.total > 0 && (
            <button
              onClick={handleClear}
              disabled={clearing}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border border-rose-200 bg-white text-rose-600 hover:bg-rose-50 hover:text-rose-700 text-[9px] uppercase tracking-wider font-bold disabled:opacity-50"
              data-testid="tally-outstanding-clear-btn"
              title="Clear all Tally outstanding data — next upload starts fresh"
            >
              <Trash size={10} weight="bold" />
              {clearing ? "…" : "Clear"}
            </button>
          )}
        </div>
        <div className="mt-3">
          <div className="font-mono tabular-nums text-xl font-bold text-slate-900" data-testid="tally-outstanding-total">
            {fmtINR(meta.total)}
          </div>
          <div className="text-[10px] uppercase tracking-wider font-bold mt-0.5 text-slate-500">
            {meta.vendor_count || 0} of {meta.total_rows || 0} vendors pending
          </div>
        </div>
        <div className="mt-3 pt-2 border-t border-slate-100">
          <div className="flex items-center gap-1.5 text-[11px]">
            <ClockClockwise size={11} weight="bold" className={tone} />
            <span className="text-slate-500">Last upload</span>
            <span className={`font-bold ml-auto ${tone}`} data-testid="tally-outstanding-upload-pill">{label}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
